import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../lib/api'
import { klassenbuchApi } from '../lib/klassenbuchApi'
import {
  Badge,
  Button,
  EmptyTableRow,
  ListToolbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TableScrollArea
} from '../components/ui'

import {
  formatCell,
  formatPeriodLabel,
  parseApiError,
  teacherOptionValue,
  type PeriodRow,
  type TeacherOpt,
  type ViewerEntry,
  type ViewerMode
} from '../lib/timetableViewerDisplay'

type TimetableInfo = {
  id: number
  label: string
  status: string
  version_no: number
  valid_from: string
  valid_until: string
  school_year_name: string
  location_name: string | null
}

type StudentOpt = { unid: string; first_name: string; last_name: string; class_name: string }

export default function TimetableViewer() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const timetableId = Number(id)

  const [error, setError] = useState<string | null>(null)
  const [loadingMeta, setLoadingMeta] = useState(true)
  const [loadingEntries, setLoadingEntries] = useState(false)
  const [meta, setMeta] = useState<{
    timetable: TimetableInfo
    periods: PeriodRow[]
    weekdays_in_use: number[]
    weekday_labels: Record<number, string>
    classes: string[]
    teachers: TeacherOpt[]
    students: StudentOpt[]
  } | null>(null)

  const [mode, setMode] = useState<ViewerMode>('class')
  const [className, setClassName] = useState('')
  const [teacherValue, setTeacherValue] = useState('')
  const [studentUnid, setStudentUnid] = useState('')
  const [entries, setEntries] = useState<ViewerEntry[]>([])

  const [subjectColorById, setSubjectColorById] = useState<Map<number, string>>(() => new Map())

  useEffect(() => {
    let cancelled = false
    klassenbuchApi
      .getSubjectColors()
      .then((data) => {
        if (cancelled) return
        const m = new Map<number, string>()
        for (const [k, v] of Object.entries(data.colors || {})) {
          const id = Number(k)
          if (Number.isFinite(id) && id > 0 && typeof v === 'string' && v) m.set(id, v)
        }
        setSubjectColorById(m)
      })
      .catch(() => {
        if (!cancelled) setSubjectColorById(new Map())
      })
    return () => {
      cancelled = true
    }
  }, [])

  const loadMeta = useCallback(async () => {
    if (!Number.isFinite(timetableId) || timetableId <= 0) {
      setError('Ungültige Stundenplan-ID')
      setLoadingMeta(false)
      return
    }
    setLoadingMeta(true)
    setError(null)
    try {
      const res = await api.get(`/timetables/${timetableId}/viewer-meta`)
      const data = res.data?.data
      if (!data?.timetable) throw new Error('Keine Metadaten')
      setMeta(data)
      const firstClass = data.classes?.[0] || ''
      setClassName(firstClass)
      const firstT = data.teachers?.[0]
      setTeacherValue(firstT ? teacherOptionValue(firstT) : '')
      const firstS = data.students?.[0]
      setStudentUnid(firstS?.unid || '')
    } catch (e) {
      setError(parseApiError(e))
      setMeta(null)
    } finally {
      setLoadingMeta(false)
    }
  }, [timetableId])

  useEffect(() => {
    void loadMeta()
  }, [loadMeta])

  const loadEntries = useCallback(async () => {
    if (!Number.isFinite(timetableId) || timetableId <= 0 || !meta) return
    setLoadingEntries(true)
    setError(null)
    try {
      if (mode === 'class' && !className.trim()) {
        setEntries([])
        return
      }
      if (mode === 'teacher' && !teacherValue) {
        setEntries([])
        return
      }
      if (mode === 'student' && !studentUnid.trim()) {
        setEntries([])
        return
      }

      const params: Record<string, string> = { view: mode }
      if (mode === 'class') {
        params.class_name = className.trim()
      } else if (mode === 'teacher') {
        if (teacherValue.startsWith('u:')) {
          params.teacher_user_id = teacherValue.slice(2)
        } else if (teacherValue.startsWith('c:')) {
          params.teacher_code = teacherValue.slice(2)
        } else {
          setEntries([])
          return
        }
      } else {
        params.student_unid = studentUnid.trim()
      }

      const res = await api.get(`/timetables/${timetableId}/viewer-entries`, { params })
      setEntries(res.data?.data?.entries || [])
    } catch (e) {
      setError(parseApiError(e))
      setEntries([])
    } finally {
      setLoadingEntries(false)
    }
  }, [timetableId, meta, mode, className, teacherValue, studentUnid])

  useEffect(() => {
    if (!meta || loadingMeta) return
    void loadEntries()
  }, [meta, loadingMeta, loadEntries])

  const cellMap = useMemo(() => {
    const m = new Map<string, ViewerEntry[]>()
    entries.forEach((e) => {
      const k = `${e.weekday}:${e.period_no}`
      if (!m.has(k)) m.set(k, [])
      m.get(k)!.push(e)
    })
    return m
  }, [entries])

  const weekdays = meta?.weekdays_in_use?.length ? meta.weekdays_in_use : [1, 2, 3, 4, 5]
  const periodRows = meta?.periods?.length
    ? [...meta.periods].sort((a, b) => a.period_no - b.period_no)
    : Array.from({ length: 10 }, (_, i) => ({
        period_no: i + 1,
        label: `${i + 1}. Stunde`,
        start_time: null,
        end_time: null
      }))

  const selectedStudentLabel = useMemo(() => {
    const s = meta?.students?.find((x) => x.unid === studentUnid)
    if (!s) return ''
    return `${s.last_name}, ${s.first_name} (${s.class_name})`
  }, [meta, studentUnid])

  if (!Number.isFinite(timetableId) || timetableId <= 0) {
    return (
      <div className="p-6">
        <p className="text-sm text-red-600">Ungültige ID.</p>
        <Button className="mt-4" variant="outline" onClick={() => navigate('/timetables')}>
          Zurück
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <ListToolbar
        title="Stundenplan anzeigen"
        description={
          meta
            ? `${meta.timetable.label} · ${meta.timetable.school_year_name}${
                meta.timetable.location_name ? ` · ${meta.timetable.location_name}` : ''
              } · gültig ${meta.timetable.valid_from} – ${meta.timetable.valid_until}`
            : 'Stundenplan wird geladen…'
        }
        trailing={
          <Link to="/timetables">
            <Button variant="outline" size="sm">
              Zurück zu den Stundenplänen
            </Button>
          </Link>
        }
      />

      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

      {loadingMeta && <p className="text-sm text-gray-600 dark:text-gray-400">Metadaten werden geladen…</p>}

      {meta && !loadingMeta && (
        <>
          <div className="card p-4 space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Ansicht:</span>
              <Button size="sm" variant={mode === 'class' ? 'primary' : 'outline'} onClick={() => setMode('class')}>
                Klassen
              </Button>
              <Button size="sm" variant={mode === 'teacher' ? 'primary' : 'outline'} onClick={() => setMode('teacher')}>
                Lehrkräfte
              </Button>
              <Button size="sm" variant={mode === 'student' ? 'primary' : 'outline'} onClick={() => setMode('student')}>
                Schüler
              </Button>
              <Badge variant={meta.timetable.status === 'active' ? 'success' : 'warning'}>{meta.timetable.status}</Badge>
              <span className="text-xs text-gray-500 dark:text-gray-400">v{meta.timetable.version_no}</span>
            </div>

            {mode === 'class' && (
              <label className="block text-sm text-gray-700 dark:text-gray-300">
                Klasse
                <select
                  className="input-field mt-1 max-w-md"
                  value={className}
                  onChange={(ev) => setClassName(ev.target.value)}
                >
                  {meta.classes.length === 0 ? <option value="">Keine Klassen im Plan</option> : null}
                  {meta.classes.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {mode === 'teacher' && (
              <label className="block text-sm text-gray-700 dark:text-gray-300">
                Lehrkraft
                <select
                  className="input-field mt-1 max-w-md"
                  value={teacherValue}
                  onChange={(ev) => setTeacherValue(ev.target.value)}
                >
                  {meta.teachers.length === 0 ? <option value="">Keine Lehrkräfte im Plan</option> : null}
                  {meta.teachers.map((t) => (
                    <option key={teacherOptionValue(t)} value={teacherOptionValue(t)}>
                      {t.display_name}
                      {t.teacher_code ? ` (${t.teacher_code})` : ''}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {mode === 'student' && (
              <div className="space-y-2">
                <label className="block text-sm text-gray-700 dark:text-gray-300">
                  Schüler
                  <select
                    className="input-field mt-1 max-w-xl"
                    value={studentUnid}
                    onChange={(ev) => setStudentUnid(ev.target.value)}
                  >
                    {meta.students.length === 0 ? (
                      <option value="">
                        Keine Schüler gefunden (Klasse in Schueler muss zu UNTIS-Klassennamen passen)
                      </option>
                    ) : null}
                    {meta.students.map((s) => (
                      <option key={s.unid} value={s.unid}>
                        {s.last_name}, {s.first_name} — {s.class_name}
                      </option>
                    ))}
                  </select>
                </label>
                {selectedStudentLabel && (
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Es wird der <strong>Klassenstundenplan</strong> der Klasse aus der Schülerzuordnung angezeigt (wie
                    Klassenansicht).
                  </p>
                )}
              </div>
            )}

            {loadingEntries && <p className="text-sm text-gray-500 dark:text-gray-400">Einträge werden geladen…</p>}
          </div>

          <section className="card p-2">
            <TableScrollArea>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell className="w-40">Stunde</TableHeaderCell>
                    {weekdays.map((wd) => (
                      <TableHeaderCell key={wd}>
                        {meta.weekday_labels?.[wd] || `WT${wd}`}
                      </TableHeaderCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {periodRows.length === 0 ? (
                    <EmptyTableRow colSpan={1 + weekdays.length} title="Keine Perioden definiert" />
                  ) : (
                    periodRows.map((p) => (
                      <TableRow key={p.period_no}>
                        <TableCell className="whitespace-normal text-sm font-medium text-gray-800 dark:text-gray-200">
                          {formatPeriodLabel(p)}
                        </TableCell>
                        {weekdays.map((wd) => {
                          const list = cellMap.get(`${wd}:${p.period_no}`) || []
                          return (
                            <TableCell key={`${wd}-${p.period_no}`} className="align-top whitespace-normal min-w-[8rem]">
                              {formatCell(list, mode, subjectColorById)}
                            </TableCell>
                          )
                        })}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableScrollArea>
          </section>
        </>
      )}
    </div>
  )
}
